<script lang="ts" setup>
import { GiftProduct } from '@/types/api/api-v3/basket'

import AppMeasureUnits from '@/components/App/AppMeasureUnits.vue'

type Props = {
  gift: GiftProduct
  isGiftSelected: boolean
  isGiftNeed: boolean | undefined
}

const props = defineProps<Props>()

const emit = defineEmits(['on-select'])

const isErrorImage = ref(false)

const image = computed(() => {
  if (!props.gift.image || isErrorImage.value) {
    return './no-image-product-card.webp'
  }

  return props.gift.image
})

const onSelect = () => emit('on-select')
</script>

<template>
  <div class="gift-card" :class="{ 'gift-card--inactive': !isGiftNeed }">
    <div
      class="gift-card__image"
      :class="{ 'gift-card__image--selected': isGiftSelected }"
    >
      <nuxt-img
        :src="image"
        :alt="gift.title"
        loading="lazy"
        format="webp"
        @error="isErrorImage = true"
      />
    </div>

    <div class="gift-card__body">
      <div class="gift-card__header">
        <span v-if="gift.title" class="gift-card__title">
          {{ gift.title }}
        </span>

        <app-measure-units
          class="gift-card__weight"
          :weight="gift.weight"
          :measure="gift.measure"
          :volume-in-liters="gift.volume_in_liters"
        />
      </div>
    </div>

    <div class="gift-card__footer">
      <the-card-button
        type="gift"
        :is-active="isGiftSelected"
        @on-add="onSelect"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.gift-card {
  display: flex;
  flex-direction: column;
  height: 236px;
  background-color: var(--ui-grey);
  border-radius: 16px;

  @include media('>=desktop-mini') {
    height: 252px;
  }

  &__image {
    position: relative;
    height: 117px;
    overflow: hidden;
    border-radius: 16px;

    @include media('>=desktop-mini') {
      height: 129px;
    }

    img {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: bottom;
    }

    &::after {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 3;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      font-weight: 800;
      line-height: 26px;
      color: var(--ui-white);
      pointer-events: none;
      visibility: hidden;
      content: '1';
      background-color: rgba(var(--ui-secondary-rgb), 0.6);
      opacity: 0;
      transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;

      @include media('>=desktop-mini') {
        font-size: 28px;
        font-weight: 900;
        line-height: 36px;
      }
    }

    &--selected {
      &::after {
        pointer-events: auto;
        visibility: visible;
        opacity: 1;
      }
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: center;
    padding: 8px;
  }

  &__header {
    display: -webkit-box;
    overflow: hidden;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: normal;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;

    @include media('>=desktop-mini') {
      line-height: 18px;
    }
  }

  &__title {
    margin-right: 4px;
    font-size: 12px;
    font-weight: 600;
    color: var(--ui-text-primary);
    word-break: break-word;

    @include media('>=desktop-mini') {
      font-size: 14px;
    }
  }

  &__weight {
    font-size: 12px;
    font-weight: 600;
    color: var(--ui-text-secondary);

    @include media('>=desktop-mini') {
      font-size: 12px;
    }
  }

  &__footer {
    padding: 0 8px 8px;
  }

  &--inactive {
    pointer-events: none;
    opacity: 0.5;
  }
}
</style>
